import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { orderBXService } from 'BoostXpressAdmin/Services/orderBXService'
import { CurrencyFormatter } from 'Helpers'
import { OrderXModel } from 'BoostXpressAdmin/Model/orderXmodel'

interface BoostXpressOrdersTableProps {
  pageNumber: number
  pageSize: number
  searchTerm: string
  status: string
  paymentStatus: string
  product: string
  userResourceId: string
  stationId: string | undefined
  startDate: string
  endDate: string
  onTotalChange: (total: number, pageSize?: number) => void
  onLoadingChange?: (loading: boolean) => void
}

const BoostXpressOrdersTable: React.FC<BoostXpressOrdersTableProps> = ({
  pageNumber,
  pageSize,
  searchTerm,
  status,
  paymentStatus,
  product,
  userResourceId,
  stationId,
  startDate,
  endDate,
  onTotalChange,
  onLoadingChange,
}) => {
  const navigate = useNavigate()
  const [orders, setOrders] = useState<OrderXModel[]>([])
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    let cancelled = false
    const fetchOrders = async () => {
      try {
        setIsLoading(true)
        onLoadingChange?.(true)
        const params = {
          page: pageNumber,
          limit: pageSize,
          sortOrder: 'DESC',
          search: searchTerm.trim() || undefined,
          status: status || undefined,
          paymentStatus: paymentStatus || undefined,
          product: product || undefined,
          userResourceId: userResourceId.trim() || undefined,
          stationId: stationId || undefined,
          ...(startDate ? { ['startDateYYYY_MM_DD']: startDate } : {}),
          ...(endDate ? { ['endDateYYYY_MM_DD']: endDate } : {}),
        }
        const res = await orderBXService.list(params as any)
        const responseData = res.data?.data
        const nextOrders = Array.isArray(responseData)
          ? responseData
          : Array.isArray(responseData?.dataset)
            ? responseData.dataset
            : []
        const pagination = res.data?.meta?.pagination ?? responseData?.meta?.pagination

        if (cancelled) return

        setOrders(nextOrders)
        onTotalChange(pagination?.total ?? responseData?.total ?? nextOrders.length)
      } catch {
        if (!cancelled) {
          setOrders([])
          onTotalChange(0)
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
          onLoadingChange?.(false)
        }
      }
    }
    fetchOrders()
    return () => {
      cancelled = true
    }
  }, [
    pageNumber,
    pageSize,
    searchTerm,
    status,
    paymentStatus,
    product,
    userResourceId,
    stationId,
    startDate,
    endDate,
    onTotalChange,
    onLoadingChange,
  ])

  return (
    <div className='bx-table-wrapper'>
      <table className='bx-table'>
        <thead>
          <tr>
            <th className='bx-finance__col-index'>#</th>
            <th className='bx-finance__col-user'>Name</th>
            <th className='bx-finance__col-reference'>Reference</th>
            <th className='bx-finance__col-amount'>Amount</th>
            <th className='bx-finance__col-status'>Status</th>
            <th className='bx-finance__col-type'>Type</th>
            <th className='bx-finance__col-date'>Date</th>
            <th className='bx-finance__col-action'>Action</th>
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={8} className='bx-finance__empty'>
                Loading...
              </td>
            </tr>
          ) : orders.length ? (
            orders.map((order, index) => {
              const offset = (pageNumber - 1) * pageSize
              return (
                <tr key={order.resourceId}>
                  <td className='bx-finance__col-index'>{offset + index + 1}</td>
                  <td className='bx-finance__col-user'>
                    {`${order.user.firstName} ${order.user.lastName}` || '-'}
                  </td>
                  <td className='bx-finance__col-reference'>{order.referenceNumber}</td>
                  <td className='bx-finance__col-amount'>
                    {`${order.currency ?? '₦'}${CurrencyFormatter(Number(order.costMajor ?? 0))}`}
                  </td>
                  <td className='bx-finance__col-status'>
                    <span
                      className={`bx-status-pill bx-status-pill--${order.status?.toLowerCase()}`}
                    >
                      {String(order.status).replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className='bx-finance__col-type'>
                    {`${order.product} • ${order.paymentStatus}`}
                  </td>
                  <td className='bx-finance__col-date'>
                    {order.createdAt ? new Date(order.createdAt).toLocaleString() : '-'}
                  </td>
                  <td className='bx-finance__col-action'>
                    <button
                      className='bx-view-link btn btn-default btn-sm'
                      onClick={() => navigate(`/boostxpress/orders/${order.resourceId}`)}
                    >
                      View
                    </button>
                  </td>
                </tr>
              )
            })
          ) : (
            <tr>
              <td colSpan={8} className='bx-finance__empty'>
                No orders found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

export default BoostXpressOrdersTable
